import { useEffect, useState } from "react";
import { ApiError, type FamilyView } from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";
import { SettingsCard } from "./SettingsCard";
import { freeConfirm, freeLabel, freedNote, isPending, placesLabel, seatStatusLabel, sentNote } from "./familySummary";

const field: React.CSSProperties = {
  font: "inherit",
  fontSize: 14,
  padding: "8px 11px",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface)",
  color: "var(--text)",
  width: "100%",
  boxSizing: "border-box",
};

const label: React.CSSProperties = { display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--text-muted)", marginBottom: 5 };

/**
 * A family pack's places (KEY-651), in the Family section of the dial's Settings tab. It used to sit in Your
 * settings beside the Subscription card, but the pack is about the dial — each relative gets one like it —
 * so it lives with the dial's other "who else can have this" settings (People, Guests, Licensing).
 *
 * The buyer invites a relative by name and email; the relative accepts from the emailed link and gets an
 * organisation and dials of their own. So this card lists names, addresses and whether each has joined —
 * never anything from inside a relative's dial, which the buyer cannot open. It hides itself entirely for an
 * organisation that is not on a family rung: the server answers 204, and there is nothing to say.
 */
export function FamilyCard({ organisationId }: { organisationId: string }) {
  const [family, setFamily] = useState<FamilyView | null | undefined>(undefined);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<{ text: string; error: boolean } | null>(null);

  const reload = async () => {
    const token = getAccessToken();
    if (!token) return;
    try {
      setFamily(await api.getFamily(token, organisationId));
    } catch {
      setFamily(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const token = getAccessToken();
    if (!token) return;
    api
      .getFamily(token, organisationId)
      .then((view) => !cancelled && setFamily(view))
      .catch(() => !cancelled && setFamily(null));
    return () => {
      cancelled = true;
    };
  }, [organisationId]);

  const run = async (key: string, action: (token: string) => Promise<string | null>) => {
    const token = getAccessToken();
    if (!token) return;
    setBusy(key);
    setNote(null);
    try {
      const done = await action(token);
      if (done) setNote({ text: done, error: false });
      await reload();
    } catch (e) {
      setNote({ text: e instanceof ApiError && e.message ? e.message : "Something went wrong. Please try again.", error: true });
    } finally {
      setBusy(null);
    }
  };

  const invite = () =>
    run("invite", async (token) => {
      const sent = await api.inviteFamilyMember(token, organisationId, { name: name.trim(), email: email.trim() });
      setName("");
      setEmail("");
      return sentNote(sent.seat.email, sent.emailSent);
    });

  // Hidden while loading, as well as when there is no pack: most organisations have none, and a card that
  // flashed "Loading…" and then vanished would be worse than one that simply appears.
  if (!family) return null;

  return (
    <SettingsCard
      title="Family"
      hint="Invite members of your family to a dial of their own. You'll see their names and whether they've joined — never what they put in their dial."
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ fontSize: 13.5, color: "var(--text-muted)" }}>
          <strong style={{ color: "var(--text)" }}>{family.planName}</strong> · {placesLabel(family)}
        </div>

        {family.seats.length > 0 && (
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
            {family.seats.map((seat) => (
              <li
                key={seat.id}
                style={{
                  display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                  padding: "10px 12px", borderRadius: "var(--radius-sm)", background: "var(--surface-inset)",
                }}
              >
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>{seat.name}</div>
                  <div style={{ fontSize: 12.5, color: "var(--text-muted)", overflowWrap: "anywhere" }}>{seat.email}</div>
                  <div style={{ fontSize: 12.5, color: seat.status === "Expired" ? "var(--st-amber, #b45309)" : "var(--text-faint)" }}>
                    {seatStatusLabel(seat)}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {isPending(seat) && (
                    <button
                      className="kd-btn kd-btn-sm"
                      disabled={busy !== null}
                      onClick={() =>
                        void run(`resend-${seat.id}`, async (token) => {
                          const sent = await api.resendFamilyInvite(token, organisationId, seat.id);
                          return sentNote(sent.seat.email, sent.emailSent);
                        })
                      }
                    >
                      {busy === `resend-${seat.id}` ? "Sending…" : "Send again"}
                    </button>
                  )}
                  <button
                    className="kd-btn kd-btn-sm"
                    disabled={busy !== null}
                    onClick={() => {
                      if (!window.confirm(freeConfirm(seat))) return;
                      void run(`revoke-${seat.id}`, async (token) => {
                        await api.revokeFamilyInvite(token, organisationId, seat.id);
                        return freedNote(seat);
                      });
                    }}
                  >
                    {freeLabel(seat)}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {family.canInvite ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void invite();
            }}
            style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 420 }}
          >
            <div>
              <label style={label} htmlFor="kd-family-name">Their name</label>
              <input id="kd-family-name" style={field} value={name} maxLength={120} disabled={busy !== null} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label style={label} htmlFor="kd-family-email">Their email address</label>
              <input
                id="kd-family-email"
                style={field}
                type="email"
                autoComplete="off"
                value={email}
                disabled={busy !== null}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <button
                className="kd-btn-primary kd-btn-sm"
                type="submit"
                disabled={busy !== null || name.trim().length === 0 || !email.includes("@")}
              >
                {busy === "invite" ? "Sending…" : "Send invitation"}
              </button>
              <span style={{ fontSize: 12.5, color: "var(--text-faint)" }}>
                They sign in with this address, so use the one they read.
              </span>
            </div>
          </form>
        ) : (
          family.cannotInviteReason && (
            <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>{family.cannotInviteReason}</p>
          )
        )}

        {note && (
          <div role="status" style={{ fontSize: 13, color: note.error ? "var(--st-overdue)" : "var(--text-muted)" }}>
            {note.text}
          </div>
        )}
      </div>
    </SettingsCard>
  );
}
